import React, { useEffect, useMemo, useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import AuthGate from '../../components/AuthGate/AuthGate';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import storeService from '../../services/store.service';
import { StoreTemplate } from '../../types/store.types';
import TemplateDetailModal from './TemplateDetailModal';
import './CreateStoreFromTemplate.scss';

/**
 * CreateStoreFromTemplate Page
 * Form to create a new store from template
 */

const CreateStoreFromTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { getUserStores, selectStore } = useStore();
  const [templates, setTemplates] = useState<StoreTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateQuery, setTemplateQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    phone: '',
    email: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailModalTemplate, setDetailModalTemplate] = useState<StoreTemplate | null>(null);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        const response = await storeService.getPublishedTemplates();
        const nextTemplates = response.data?.templates || [];
        setTemplates(nextTemplates);
        if (nextTemplates[0]?.id) {
          setSelectedTemplateId(nextTemplates[0].id);
        }
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Impossible de charger les templates disponibles');
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  const visibleTemplates = useMemo(() => {
    const q = templateQuery.trim().toLowerCase();
    if (!q) {
      return templates;
    }
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, templateQuery]);

  useEffect(() => {
    if (visibleTemplates.length === 0) {
      setSelectedTemplateId(null);
      return;
    }
    const stillSelected =
      selectedTemplateId != null && visibleTemplates.some((t) => t.id === selectedTemplateId);
    if (!stillSelected) {
      setSelectedTemplateId(visibleTemplates[0].id);
    }
  }, [visibleTemplates, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (error) {
      setError(null);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors = {
      name: '',
      phone: '',
      email: '',
    };

    if (!formData.name) {
      errors.name = 'Le nom du store est requis';
    } else if (formData.name.length < 2) {
      errors.name = 'Le nom doit contenir au moins 2 caractères';
    }

    if (formData.phone && !/^[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Le téléphone doit contenir 10-15 chiffres';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    setFormErrors(errors);
    return !errors.name && !errors.phone && !errors.email;
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (!selectedTemplateId) {
        throw new Error('Veuillez choisir un template');
      }

      const response = await storeService.createStoreFromTemplate({
        ...formData,
        templateStoreId: selectedTemplateId
      });
      const store = response.data?.store;
      
      if (!store || !store.id) {
        throw new Error('Store creation failed: no store returned');
      }
      
      // Reload stores and select the new one
      await getUserStores();
      selectStore(store.id);
      
      // Navigate to orders page after successful creation
      navigate('/orders');
    } catch (err: any) {
      console.error('Create store from template error:', err);
      setError(err.response?.data?.error?.message || 'Erreur lors de la création du store depuis le template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/stores');
  };

  return (
    <>
    <AuthGate wide homeTo="/stores">
      <Link className="auth-back" to="/stores">Stores</Link>
      <p className="auth-kicker">Nouveau</p>
      <h1 className="auth-h">Depuis un modèle</h1>
      <p className="auth-sub">
        Reprenez un cadre déjà réglé. <Link to="/stores/create">Avec l'assistant</Link>
      </p>

          {selectedTemplate && !isLoadingTemplates && templates.length > 0 && (
            <div className="create-store-template-picked">
              <span className="create-store-template-picked__label">Modèle sélectionné</span>
              <div className="create-store-template-picked__head">
                <strong className="create-store-template-picked__name">{selectedTemplate.name}</strong>
                <button
                  type="button"
                  className="create-store-template-picked__eye"
                  title="Détail du paramétrage"
                  aria-label="Voir le détail du template"
                  onClick={() => setDetailModalTemplate(selectedTemplate)}
                >
                  <Icon name="eye" size={18} />
                </button>
              </div>
              {selectedTemplate.description && (
                <p className="create-store-template-picked__description">{selectedTemplate.description}</p>
              )}
            </div>
          )}

          {error && (
            <div className="create-store-template-alert create-store-template-alert--error">
              <Icon name="alert-circle" size={18} />
              <span>{error}</span>
            </div>
          )}

          <form className="create-store-template-form create-store-template-form--split" onSubmit={handleSubmit}>
            <div className="create-store-template-form__column create-store-template-form__column--picker">
              <div className="create-store-template-form__group">
                <label className="create-store-template-form__label" htmlFor="template-search">
                  Choisir un modèle
                </label>
                {isLoadingTemplates ? (
                  <div className="create-store-template-empty">Chargement des modèles…</div>
                ) : templates.length === 0 ? (
                  <div className="create-store-template-empty">
                    Aucun modèle publié n&apos;est disponible pour le moment.
                  </div>
                ) : (
                  <>
                    <div className="create-store-template-search">
                      <Icon name="search" size={18} />
                      <input
                        id="template-search"
                        type="search"
                        className="create-store-template-search__input"
                        placeholder="Rechercher par nom ou description…"
                        value={templateQuery}
                        onChange={(e) => setTemplateQuery(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {visibleTemplates.length === 0 ? (
                      <div className="create-store-template-empty">Aucun résultat pour cette recherche.</div>
                    ) : (
                      <div className="create-store-template-selector">
                        {visibleTemplates.map((template) => (
                          <div
                            key={template.id}
                            className={`create-store-template-selector__row ${selectedTemplateId === template.id ? 'create-store-template-selector__row--active' : ''}`}
                          >
                            <button
                              type="button"
                              className={`create-store-template-selector__item ${selectedTemplateId === template.id ? 'create-store-template-selector__item--active' : ''}`}
                              onClick={() => setSelectedTemplateId(template.id)}
                            >
                              <div className="create-store-template-selector__title-row">
                                <span className="create-store-template-selector__title">{template.name}</span>
                                <span className="create-store-template-selector__badge">ZEEDCOM</span>
                              </div>
                            </button>
                            <button
                              type="button"
                              className="create-store-template-selector__eye"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDetailModalTemplate(template);
                              }}
                              aria-label={`Détail du modèle ${template.name}`}
                              title="Voir le paramétrage du modèle"
                            >
                              <Icon name="eye" size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-store-template-form__column create-store-template-form__column--fields">
            <Input
              label="Nom du Store"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={formErrors.name}
              placeholder="Mon nouveau store"
              required
              fullWidth
            />

            <div className="create-store-template-form__group">
              <label htmlFor="description" className="create-store-template-form__label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Décrivez votre store..."
                className="create-store-template-form__textarea"
                rows={3}
              />
            </div>

            <Input
              label="Adresse"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Rue Principale, Ville, Pays"
              fullWidth
            />

            <Input
              label="Téléphone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={formErrors.phone}
              placeholder="0612345678"
              fullWidth
            />

            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={formErrors.email}
              placeholder="contact@mystore.com"
              fullWidth
            />

            <div className="create-store-template-form__actions">
              <Button
                type="button"
                variant="outline"
                size="large"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Annuler
              </Button>

              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={isLoading}
                disabled={!selectedTemplateId || templates.length === 0 || visibleTemplates.length === 0}
              >
                <Icon name="layers" size={18} /> Créer depuis le modèle
              </Button>
            </div>
            </div>
          </form>
    </AuthGate>
      <TemplateDetailModal
        open={detailModalTemplate != null}
        templateId={detailModalTemplate?.id ?? null}
        templateName={detailModalTemplate?.name ?? ''}
        onClose={() => setDetailModalTemplate(null)}
      />
    </>
  );
};

export default CreateStoreFromTemplate;

